import gsap from "gsap";
import { Howl } from "howler";

const popSound = new Howl({
  src: ["./sounds/pop-sound.mp3"],
  preload: true, // Add preload to ensure it's ready
  volume: 0.15,
  fade: true,
});

export class DarkModeManager {
  constructor(bakedMaterial, gui, renderer, debugObject) {
    this.bakedMaterial = bakedMaterial;
    this.gui = gui;
    this.gsap = gsap;
    this.renderer = renderer;
    this.debugObject = debugObject;
    this.isDarkMode = false;
    this.toggleButton = document.querySelector(".toggle-theme");
    this.soundEnabled = true; // Add sound toggle flag

    this.init();
  }

  init() {
    // Set up GUI controls
    this.setupGUI();

    // Set up event listeners
    this.setupEventListeners();

    // Set initial state
    this.updateTheme();
  }

  setupGUI() {
    const themeFolder = this.gui.addFolder("Theme");
    themeFolder
      .add(this.bakedMaterial.uniforms.uNightMix, "value")
      .min(0)
      .max(1)
      .name("Night Mix")
      .onChange((value) => {
        this.isDarkMode = value === 0;
        this.updateTheme(); // Update everything including clear color
      });

    // Add clear color controls to GUI
    const rendererFolder = this.gui.addFolder("Renderer");
    rendererFolder
      .addColor(this.debugObject, "clearColor1")
      .name("Day BG Color")
      .onChange(() => {
        if (this.isDarkMode) {
          this.renderer.setClearColor(this.debugObject.clearColor1);
        }
      });
    rendererFolder
      .addColor(this.debugObject, "clearColor2")
      .name("Night BG Color")
      .onChange(() => {
        if (!this.isDarkMode) {
          this.renderer.setClearColor(this.debugObject.clearColor2);
        }
      });
  }

  setupEventListeners() {
    this.toggleButton.addEventListener("click", () => this.toggle());

    // Optional: Listen for system preference changes
    if (window.matchMedia) {
      const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
      mediaQuery.addEventListener("change", (e) => {
        this.isDarkMode = e.matches;
        this.updateTheme();
      });

      // Set initial state based on system preference
      this.isDarkMode = mediaQuery.matches;
      this.updateTheme();
    }

    // Hover Animation
    this.toggleButton.addEventListener("pointerenter", () => {
      this.gsap.to(this.toggleButton, {
        scale: 1.5,
        ease: "back.out(2)",
        duration: 0.5,
        rotate: "-15deg",
      });
    });

    this.toggleButton.addEventListener("pointerleave", () => {
      this.gsap.to(this.toggleButton, {
        scale: 1,
        ease: "back.out(2)",
        duration: 0.5,
        rotate: 0,
      });
    });
  }

  toggle() {
    this.isDarkMode = !this.isDarkMode;

    // Play pop sound when toggling
    this.playToggleSound();

    // Add click animation
    this.animateToggle();

    this.updateTheme();
  }

  playToggleSound() {
    if (this.soundEnabled && popSound) {
      // Stop any currently playing instance to avoid overlap
      popSound.stop();

      // Play the sound
      popSound.play();

      // Optional: Add slight random pitch variation for more natural sound
      const pitch = 0.9 + Math.random() * 0.2; // Between 0.9 and 1.1
      popSound.rate(pitch);
    }
  }

  animateToggle() {}

  updateTheme() {
    // Update material uniform
    this.bakedMaterial.uniforms.uNightMix.value = this.isDarkMode ? 0 : 1;

    // Update renderer clear color based on theme
    const clearColor = this.isDarkMode
      ? this.debugObject.clearColor1
      : this.debugObject.clearColor2;

    this.renderer.setClearColor(clearColor);

    // Update GUI to reflect changes
    this.bakedMaterial.uniforms.uNightMix.needsUpdate = true;

    // Update DOM
    this.updateDOM();
  }

  updateDOM() {
    if (this.isDarkMode) {
      document.body.classList.add("dark");
      this.toggleButton.setAttribute("aria-pressed", "true");
      this.toggleButton.setAttribute("aria-label", "Switch to light mode");
    } else {
      document.body.classList.remove("dark");
      this.toggleButton.setAttribute("aria-pressed", "false");
      this.toggleButton.setAttribute("aria-label", "Switch to dark mode");
    }
  }

  // Public method to get current state
  getIsDarkMode() {
    return this.isDarkMode;
  }

  // Public method to set state programmatically
  setDarkMode(isDark) {
    this.isDarkMode = isDark;
    this.updateTheme();
  }

  // Method to enable/disable sound
  setSoundEnabled(enabled) {
    this.soundEnabled = enabled;
  }
}
